import { supabase } from "@/integrations/supabase/client";

// Rate limiting configuration
const MAX_ERRORS_PER_MINUTE = 5;
const RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
const ERROR_DEDUP_WINDOW = 5 * 60 * 1000; // 5 minutes

// In-memory tracking for rate limiting and deduplication
let errorCount = 0;
let windowStart = Date.now();
const recentErrors = new Map<string, number>();

// Generate a hash for error deduplication
const hashError = (message: string, stack?: string): string => {
  const data = `${message}|${stack || ""}`;
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

// Check if we should report this error (rate limiting + dedup)
const shouldReportError = (errorHash: string): boolean => {
  const now = Date.now();
  
  // Reset rate limit window if expired
  if (now - windowStart > RATE_LIMIT_WINDOW) {
    errorCount = 0;
    windowStart = now;
  }
  
  // Check rate limit
  if (errorCount >= MAX_ERRORS_PER_MINUTE) {
    console.warn("Error reporting rate limit reached, skipping");
    return false;
  }
  
  // Check for duplicate errors within window
  const lastReported = recentErrors.get(errorHash);
  if (lastReported && now - lastReported < ERROR_DEDUP_WINDOW) {
    console.warn("Duplicate error within dedup window, skipping");
    return false;
  }
  
  // Clean old entries from dedup map
  for (const [hash, timestamp] of recentErrors.entries()) {
    if (now - timestamp > ERROR_DEDUP_WINDOW) {
      recentErrors.delete(hash);
    }
  }
  
  return true;
};

// Global error handler for uncaught errors
export function setupGlobalErrorHandler() {
  // Handle uncaught JavaScript errors
  window.onerror = async (message, source, lineno, colno, error) => {
    if (import.meta.env.DEV) {
      console.error("Global error:", { message, source, lineno, colno, error });
    }
    
    const errorHash = hashError(String(message), error?.stack);
    if (!shouldReportError(errorHash)) return false;
    
    await reportError({
      error_message: String(message),
      error_stack: error?.stack || `at ${source}:${lineno}:${colno}`,
      error_url: window.location.href,
    });
    
    errorCount++;
    recentErrors.set(errorHash, Date.now());
    
    return false;
  };

  // Handle unhandled promise rejections
  window.onunhandledrejection = async (event) => {
    if (import.meta.env.DEV) {
      console.error("Unhandled rejection:", event.reason);
    }
    
    const errorMessage = event.reason?.message || String(event.reason);
    const errorHash = hashError(errorMessage, event.reason?.stack);
    if (!shouldReportError(errorHash)) return;
    
    await reportError({
      error_message: errorMessage,
      error_stack: event.reason?.stack,
      error_url: window.location.href,
    });
    
    errorCount++;
    recentErrors.set(errorHash, Date.now());
  };
}

interface ErrorReport {
  error_message: string;
  error_stack?: string;
  error_url?: string;
}

export async function reportError(errorData: ErrorReport) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    await supabase.functions.invoke("send-error-notification", {
      body: {
        ...errorData,
        user_id: user?.id,
        user_agent: navigator.userAgent,
      },
    });
  } catch (reportError) {
    // Silently fail to avoid infinite error loops
    console.error("Failed to report error:", reportError);
  }
}
